import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, Check, XCircle, PencilSimple } from "@phosphor-icons/react";
import BranchFilter from "@/components/BranchFilter";
import { useAuth } from "@/context/AuthContext";

const APP_STATUS_TABS = [
  { v: "pending", label: "Pending", color: "border-amber-500 text-amber-600" },
  { v: "approved", label: "Approved", color: "border-[#00B341] text-[#00B341]" },
  { v: "rejected", label: "Rejected", color: "border-destructive text-destructive" },
];

function StatusBadge({ v }) {
  const opt = APP_STATUS_TABS.find((x) => x.v === v);
  return (
    <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${opt?.color || "border-canvas-line"}`}>
      {v || "—"}
    </span>
  );
}

// ============ Apply Leave Dialog ============
function ApplyLeaveDialog({ open, employees, leaveTypes, onClose, onCreated }) {
  const [form, setForm] = useState({
    employee_id: "", leave_type_id: "", from_date: "", to_date: "", days: 1, reason: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        employee_id: employees[0]?.id || "",
        leave_type_id: leaveTypes[0]?.id || "",
        from_date: new Date().toISOString().slice(0, 10),
        to_date: new Date().toISOString().slice(0, 10),
        days: 1,
        reason: "",
      });
    }
  }, [open, employees, leaveTypes]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.leave_type_id || !form.from_date || !form.to_date || form.days <= 0) {
      toast.error("Fill all required fields");
      return;
    }
    setSaving(true);
    try {
      await api.post("/hr/leave-applications", form);
      toast.success("Leave applied");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="hr-apply-leave-dialog">
      <div className="bg-canvas border border-canvas-line max-w-lg w-full">
        <div className="px-6 py-4 border-b border-canvas-line">
          <div className="text-sm font-semibold">Apply for Leave</div>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Employee *</span>
            <select value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} data-testid="hr-apply-emp" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
              <option value="">— Choose —</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.employee_code} · {e.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Leave Type *</span>
            <select value={form.leave_type_id} onChange={(e) => set("leave_type_id", e.target.value)} data-testid="hr-apply-type" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
              {leaveTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.code} · {t.name}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-3 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">From *</span>
              <input type="date" value={form.from_date} onChange={(e) => set("from_date", e.target.value)} data-testid="hr-apply-from" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">To *</span>
              <input type="date" value={form.to_date} onChange={(e) => set("to_date", e.target.value)} data-testid="hr-apply-to" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Days *</span>
              <input type="number" step="0.5" min="0.5" value={form.days} onChange={(e) => set("days", parseFloat(e.target.value) || 0)} data-testid="hr-apply-days" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Reason</span>
            <textarea rows={2} value={form.reason} onChange={(e) => set("reason", e.target.value)} data-testid="hr-apply-reason" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
          </label>
          <div className="pt-2 flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="hr-apply-submit" className="px-4 py-2 bg-ink text-white text-sm hover:bg-brand disabled:opacity-50">
              {saving ? "Submitting…" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ Leave Types Config Panel ============
function LeaveTypesPanel({ leaveTypes, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", annual_quota: 0, is_paid: true, active: true });

  const openCreate = () => { setEditing(null); setForm({ code: "", name: "", annual_quota: 0, is_paid: true, active: true }); setShowForm(true); };
  const openEdit = (t) => { setEditing(t); setForm({ code: t.code, name: t.name, annual_quota: t.annual_quota, is_paid: t.is_paid, active: t.active }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/hr/leave-types/${editing.id}`, form);
        toast.success("Leave type updated");
      } else {
        await api.post("/hr/leave-types", form);
        toast.success("Leave type created");
      }
      setShowForm(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">Leave Types</div>
        <button
          type="button"
          onClick={openCreate}
          data-testid="hr-lt-new"
          className="px-3 py-1.5 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1.5"
        >
          <Plus size={11} weight="bold" /> New type
        </button>
      </div>
      <div className="border border-canvas-line overflow-hidden">
        <table className="w-full" data-testid="hr-leave-types-table">
          <thead>
            <tr className="border-b border-canvas-line bg-canvas-panel">
              {["Code", "Name", "Annual Quota", "Paid", "Active", ""].map((h) => (
                <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leaveTypes.map((t) => (
              <tr key={t.id} className="border-b border-canvas-line hover:bg-canvas-panel/30">
                <td className="px-3 py-2 font-mono text-[11px] font-semibold">{t.code}</td>
                <td className="px-3 py-2 text-sm">{t.name}</td>
                <td className="px-3 py-2 font-mono text-sm tabular-nums">{t.annual_quota || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{t.is_paid ? "YES" : "NO"}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{t.active ? "YES" : "NO"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => openEdit(t)} className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4">
          <div className="bg-canvas border border-canvas-line max-w-md w-full">
            <div className="px-6 py-4 border-b border-canvas-line text-sm font-semibold">{editing ? "Edit" : "New"} Leave Type</div>
            <form onSubmit={submit} className="p-6 space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Code *</span>
                <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" placeholder="CL / SL / EL" required />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Name *</span>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" required />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Annual Quota (0 = unlimited)</span>
                <input type="number" step="0.5" value={form.annual_quota} onChange={(e) => setForm({ ...form, annual_quota: parseFloat(e.target.value) || 0 })} className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_paid} onChange={(e) => setForm({ ...form, is_paid: e.target.checked })} /> Paid leave
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
              </label>
              <div className="pt-2 flex items-center justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-canvas-line text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-ink text-white text-sm hover:bg-brand">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Admin Amend Dialog ============
function AmendLeaveDialog({ open, application, employees, leaveTypes, onClose, onSaved }) {
  const [form, setForm] = useState({
    leave_type_id: "",
    from_date: "",
    to_date: "",
    days: 1,
    status: "approved",
    reason: "",
    reason_for_amendment: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && application) {
      setForm({
        leave_type_id: application.leave_type_id || "",
        from_date: application.from_date || "",
        to_date: application.to_date || "",
        days: application.days || 1,
        status: application.status || "approved",
        reason: application.reason || "",
        reason_for_amendment: "",
      });
    }
  }, [open, application]);

  if (!open || !application) return null;

  const emp = employees.find((e) => e.id === application.employee_id);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reason_for_amendment.trim()) {
      toast.error("Please describe why this leave is being amended (audit log)");
      return;
    }
    if (!form.from_date || !form.to_date || form.days <= 0) {
      toast.error("Dates and days are required");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/hr/leave-applications/${application.id}/admin-amend`, form);
      toast.success("Leave amended — balance & attendance re-synced");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="amend-leave-modal">
      <div className="bg-white w-full max-w-lg border border-canvas-line">
        <div className="px-5 py-3 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Admin — Amend Leave</div>
            <div className="text-sm font-semibold">{emp?.name || "—"} · {emp?.employee_code || ""}</div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Leave Type</label>
            <select
              value={form.leave_type_id}
              onChange={(e) => set("leave_type_id", e.target.value)}
              className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm"
              data-testid="amend-leave-type"
            >
              {leaveTypes.map((lt) => <option key={lt.id} value={lt.id}>{lt.code} · {lt.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">From</label>
              <input type="date" value={form.from_date} onChange={(e) => set("from_date", e.target.value)}
                className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm" data-testid="amend-leave-from" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">To</label>
              <input type="date" value={form.to_date} onChange={(e) => set("to_date", e.target.value)}
                className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm" data-testid="amend-leave-to" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Days</label>
              <input type="number" step="0.5" min="0.5" value={form.days}
                onChange={(e) => set("days", parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm" data-testid="amend-leave-days" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
              className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm"
              data-testid="amend-leave-status"
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Reason (employee-facing)</label>
            <textarea rows={2} value={form.reason} onChange={(e) => set("reason", e.target.value)}
              className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm" data-testid="amend-leave-reason" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">
              Reason for Amendment <span className="text-destructive">*</span>
            </label>
            <textarea rows={2} value={form.reason_for_amendment}
              onChange={(e) => set("reason_for_amendment", e.target.value)}
              placeholder="Why is this being changed? (goes into audit log)"
              className="w-full px-3 py-2 border border-canvas-line bg-canvas text-sm"
              data-testid="amend-leave-audit-reason" />
          </div>
          <div className="text-[11px] text-ink-muted bg-amber-50 border border-amber-200 p-2">
            Amending an approved leave will reverse the previous balance decrement + auto-attendance rows, then re-apply the new values. Attendance rows manually marked by managers are NOT touched.
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="amend-leave-save"
              className="px-4 py-2 bg-ink text-white text-sm hover:bg-brand disabled:opacity-50">
              {saving ? "Saving…" : "Save Amendment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ Main Page ============
export default function HrLeavesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("applications");
  const [statusTab, setStatusTab] = useState("pending");
  const [branchId, setBranchId] = useState("");
  const [applications, setApplications] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [amendApp, setAmendApp] = useState(null); // application being amended

  const empById = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e.id] = e; });
    return m;
  }, [employees]);
  const ltById = useMemo(() => {
    const m = {};
    leaveTypes.forEach((t) => { m[t.id] = t; });
    return m;
  }, [leaveTypes]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const appParams = { status: statusTab };
      if (branchId) appParams.branch_id = branchId;
      const empParams = { limit: 500, skip: 0, status: "active" };
      if (branchId) empParams.branch_id = branchId;
      const [appsRes, ltRes, empRes] = await Promise.all([
        api.get("/hr/leave-applications", { params: appParams }),
        api.get("/hr/leave-types"),
        api.get("/hr/employees", { params: empParams }),
      ]);
      setApplications(appsRes.data);
      setLeaveTypes(ltRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusTab, branchId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (app, decision) => {
    let reason = "";
    if (decision === "reject") {
      reason = window.prompt("Reject reason (optional):") || "";
    }
    try {
      await api.post(`/hr/leave-applications/${app.id}/decide`, { decision, reason });
      toast.success(`Leave ${decision === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="HR / 03"
        title="Leaves"
        subtitle="Apply, approve, and configure leave types. Approving auto-decrements the running yearly balance for that employee + type."
        actions={
          <button
            onClick={() => setShowApply(true)}
            data-testid="hr-leaves-apply-open"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold hover:bg-brand flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            Apply for Leave
          </button>
        }
      />

      <div className="px-8 py-6 space-y-4">
        <div className="flex border border-canvas-line w-fit">
          {[
            { k: "applications", label: "Applications" },
            { k: "types", label: "Configure Leave Types" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`hr-leaves-tab-${t.k}`}
              className={`px-4 py-1.5 text-xs font-mono uppercase tracking-wider ${tab === t.k ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "applications" && (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <BranchFilter value={branchId} onChange={setBranchId} testId="hr-leaves-branch" />
              <div className="flex border border-canvas-line w-fit">
                {APP_STATUS_TABS.map((t) => (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => setStatusTab(t.v)}
                    data-testid={`hr-leaves-status-${t.v}`}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${statusTab === t.v ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-canvas-card border border-canvas-line overflow-hidden">
              <table className="w-full" data-testid="hr-leave-apps-table">
                <thead>
                  <tr className="border-b border-canvas-line bg-canvas-panel">
                    {["Employee", "Type", "From → To", "Days", "Reason", "Status", "Actions"].map((h) => (
                      <th key={h} className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">Loading…</td></tr>
                  )}
                  {!loading && applications.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center font-mono text-xs text-ink-subtle">No leave applications in &ldquo;{statusTab}&rdquo;.</td></tr>
                  )}
                  {applications.map((a) => {
                    const emp = empById[a.employee_id];
                    const lt = ltById[a.leave_type_id];
                    return (
                      <tr key={a.id} className="border-b border-canvas-line hover:bg-canvas-panel/30" data-testid={`hr-leave-app-row-${a.id}`}>
                        <td className="px-3 py-2">
                          <div className="text-sm font-semibold">{emp?.name || "—"}</div>
                          <div className="font-mono text-[10px] text-ink-muted">{emp?.employee_code || "—"}</div>
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px]">{lt?.code || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[11px] tabular-nums">{a.from_date} → {a.to_date}</td>
                        <td className="px-3 py-2 font-mono text-sm tabular-nums font-semibold">{a.days}</td>
                        <td className="px-3 py-2 text-sm text-ink truncate max-w-[220px]">{a.reason || "—"}</td>
                        <td className="px-3 py-2"><StatusBadge v={a.status} /></td>
                        <td className="px-3 py-2">
                          {a.status === "pending" ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => decide(a, "approve")}
                                data-testid={`hr-leave-approve-${a.id}`}
                                className="px-2 py-1 border border-[#00B341] text-[#00B341] text-[10px] font-mono uppercase tracking-wider hover:bg-[#00B341] hover:text-white flex items-center gap-1"
                              >
                                <Check size={10} weight="bold" /> Approve
                              </button>
                              <button
                                onClick={() => decide(a, "reject")}
                                data-testid={`hr-leave-reject-${a.id}`}
                                className="px-2 py-1 border border-destructive text-destructive text-[10px] font-mono uppercase tracking-wider hover:bg-destructive hover:text-white flex items-center gap-1"
                              >
                                <XCircle size={10} weight="bold" /> Reject
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="font-mono text-[10px] text-ink-muted">
                                by {a.approved_by || "—"}
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={() => setAmendApp(a)}
                                  data-testid={`hr-leave-amend-${a.id}`}
                                  className="px-2 py-1 border border-canvas-line text-ink-muted text-[10px] font-mono uppercase tracking-wider hover:border-ink hover:text-ink flex items-center gap-1"
                                  title="Admin — amend this leave (adjusts balance & attendance)"
                                >
                                  <PencilSimple size={10} weight="bold" /> Amend
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "types" && (
          <LeaveTypesPanel leaveTypes={leaveTypes} onChanged={load} />
        )}
      </div>

      <ApplyLeaveDialog
        open={showApply}
        employees={employees}
        leaveTypes={leaveTypes.filter((t) => t.active)}
        onClose={() => setShowApply(false)}
        onCreated={load}
      />

      <AmendLeaveDialog
        open={!!amendApp}
        application={amendApp}
        employees={employees}
        leaveTypes={leaveTypes}
        onClose={() => setAmendApp(null)}
        onSaved={load}
      />
    </div>
  );
}
